import React, { useState, useEffect } from 'react';
import { Card, Nav, Form, Row, Col, Button, Alert, Spinner, Tab } from 'react-bootstrap';
import { FaUpload, FaSave, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import axios from 'axios';
import ServicesManagement from './ServicesManagement';
import './HomeManagement.scss';

const HomeManagement = () => {
  const [key, setKey] = useState('hero');
  const [loading, setLoading] = useState({
    hero: false,
    vision: false,
    mission: false,
    carousel: false
  });
  const [error, setError] = useState({
    hero: null,
    vision: null,
    mission: null,
    carousel: null
  });
  const [success, setSuccess] = useState({
    hero: null,
    vision: null,
    mission: null,
    carousel: null
  });
  
  const [heroTab, setHeroTab] = useState('single');

  // Sample data - replace with actual data from backend
  const [heroData, setHeroData] = useState({
    title: '',
    subtitle: '',
    image: null,
    mobile_image: null,
    tablet_image: null
  });
  
  const [carouselImages, setCarouselImages] = useState([]);
  const [useCarouselOption, setUseCarouselOption] = useState(false);
  const [activeImageTab, setActiveImageTab] = useState('desktop');
  const [carouselImageTab, setCarouselImageTab] = useState('desktop');
  const [carouselImageData, setCarouselImageData] = useState({
    image: null,
    mobile_image: null,
    tablet_image: null,
    device_type: 'all'
  });

  const [visionData, setVisionData] = useState({
    header: 'Our Vision',
    paragraph: '',
    img: 'https://via.placeholder.com/800x400',
    image_path: ''
  });

  const [missionData, setMissionData] = useState({
    header: 'Our Mission',
    paragraph: 'To help people find their perfect properties while delivering outstanding customer service.',
    img: 'https://via.placeholder.com/800x400',
    image_path: ''
  });

  useEffect(() => {
    fetchHeroData();
    fetchVisionData();
    fetchMissionData();
  }, []);

  const fetchHeroData = async () => {
    try {
      const response = await axios.get('https://realestate.bonlineco.com/api/hero');
      if (response.data.status === 'success' && response.data.data) {
        setHeroData({
          title: response.data.data.title || '',
          subtitle: response.data.data.subtitle || '',
          image: null
        });
        
        if (response.data.carousel_images) {
          setCarouselImages(response.data.carousel_images);
          
          // Check if we should use carousel (no single image but has carousel images)
          setUseCarouselOption(!response.data.data.image_path && response.data.carousel_images.length > 0);
        }
      }
    } catch (error) {
      console.error('Error fetching hero data:', error);
      setError({ ...error, hero: 'Failed to fetch hero data' });
    }
  };

  const fetchVisionData = async () => {
    try {
      setLoading({ ...loading, vision: true });
      const response = await axios.get('https://realestate.bonlineco.com/api/vision');
      if (response.data.status === 'success' && response.data.data) {
        setVisionData({
          header: 'Our Vision',
          paragraph: response.data.data.paragraph || '',
          img: response.data.data.image_path ? `https://realestate.bonlineco.com/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : 'https://via.placeholder.com/800x400',
          image_path: response.data.data.image_path || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch vision data:', error);
      setError({ ...error, vision: 'Failed to load vision data' });
    } finally {
      setLoading({ ...loading, vision: false });
    }
  };

  const fetchMissionData = async () => {
    try {
      setLoading({ ...loading, mission: true });
      const response = await axios.get('https://realestate.bonlineco.com/api/mission');
      if (response.data.status === 'success' && response.data.data) {
        setMissionData({
          header: 'Our Mission',
          paragraph: response.data.data.paragraph || '',
          img: response.data.data.image_path ? `https://realestate.bonlineco.com/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : 'https://via.placeholder.com/800x400',
          image_path: response.data.data.image_path || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch mission data:', error);
      setError({ ...error, mission: 'Failed to load mission data' });
    } finally {
      setLoading({ ...loading, mission: false });
    }
  };

  const handleHeroSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, hero: true });
    setError({ ...error, hero: null });
    setSuccess({ ...success, hero: null });

    const formData = new FormData();
    formData.append('title', heroData.title);
    formData.append('subtitle', heroData.subtitle);
    
    // Append images for different devices if they exist
    if (heroData.image) {
      formData.append('image', heroData.image);
    }
    
    if (heroData.mobile_image) {
      formData.append('mobile_image', heroData.mobile_image);
    }
    
    if (heroData.tablet_image) {
      formData.append('tablet_image', heroData.tablet_image);
    }

    try {
      const response = await axios.post('https://realestate.bonlineco.com/api/hero', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'success') {
        setHeroData({ 
          ...heroData, 
          image: null,
          mobile_image: null,
          tablet_image: null
        });
        setSuccess({ ...success, hero: 'Hero section updated successfully!' });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, hero: null }));
        }, 5000);
      }
    } catch (error) {
      console.error('Error updating hero:', error);
      setError({ ...error, hero: 'Failed to update hero section' });
    } finally {
      setLoading({ ...loading, hero: false });
    }
  };

  const handleImageUpload = async (section, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (section === 'vision') {
      try {
        setLoading({ ...loading, vision: true });
        setError({ ...error, vision: null });
        setSuccess({ ...success, vision: null });
        const formData = new FormData();
        formData.append('image', file);
        formData.append('paragraph', visionData.paragraph);

        const response = await axios.post('https://realestate.bonlineco.com/api/vision', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.status === 'success') {
          setVisionData({
            ...visionData,
            img: `https://realestate.bonlineco.com/api/image/${response.data.data.image_path.replace(/\\/g, '/')}`,
            image_path: response.data.data.image_path
          });
          
          setSuccess({ ...success, vision: 'Vision image updated successfully!' });
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccess(prev => ({ ...prev, vision: null }));
          }, 5000);
        }
      } catch (error) {
        console.error('Failed to upload image:', error);
        setError({ ...error, vision: error.response?.data?.message || 'Failed to upload image' });
      } finally {
        setLoading({ ...loading, vision: false });
      }
    } else if (section === 'mission') {
      try {
        setLoading({ ...loading, mission: true });
        setError({ ...error, mission: null });
        setSuccess({ ...success, mission: null });
        const formData = new FormData();
        formData.append('image', file);
        formData.append('paragraph', missionData.paragraph);

        const response = await axios.post('https://realestate.bonlineco.com/api/mission', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.status === 'success') {
          setMissionData({
            ...missionData,
            img: `https://realestate.bonlineco.com/api/image/${response.data.data.image_path.replace(/\\/g, '/')}`,
            image_path: response.data.data.image_path
          });
          
          setSuccess({ ...success, mission: 'Mission image updated successfully!' });
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccess(prev => ({ ...prev, mission: null }));
          }, 5000);
        }
      } catch (error) {
        console.error('Failed to upload image:', error);
        setError({ ...error, mission: error.response?.data?.message || 'Failed to upload image' });
      } finally {
        setLoading({ ...loading, mission: false });
      }
    } else {
      // Handle other sections' image upload
      console.log(`Uploading image for ${section}:`, file);
    }
  };

  const handleSave = async (section) => {
    if (section === 'vision') {
      try {
        setLoading({ ...loading, vision: true });
        setError({ ...error, vision: null });
        setSuccess({ ...success, vision: null });
        const formData = new FormData();
        formData.append('paragraph', visionData.paragraph);

        const response = await axios.post('https://realestate.bonlineco.com/api/vision', formData);

        if (response.data.status === 'success') {
          setVisionData({
            ...visionData,
            paragraph: response.data.data.paragraph
          });
          
          setSuccess({ ...success, vision: 'Vision content updated successfully!' });
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccess(prev => ({ ...prev, vision: null }));
          }, 5000);
        }
      } catch (error) {
        console.error('Failed to save vision data:', error);
        setError({ ...error, vision: error.response?.data?.message || 'Failed to save changes' });
      } finally {
        setLoading({ ...loading, vision: false });
      }
    } else if (section === 'mission') {
      try {
        setLoading({ ...loading, mission: true });
        setError({ ...error, mission: null });
        setSuccess({ ...success, mission: null });
        const formData = new FormData();
        formData.append('paragraph', missionData.paragraph);

        const response = await axios.post('https://realestate.bonlineco.com/api/mission', formData);

        if (response.data.status === 'success') {
          setMissionData({
            ...missionData,
            paragraph: response.data.data.paragraph
          });
          
          setSuccess({ ...success, mission: 'Mission content updated successfully!' });
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccess(prev => ({ ...prev, mission: null }));
          }, 5000);
        }
      } catch (error) {
        console.error('Failed to save mission data:', error);
        setError({ ...error, mission: error.response?.data?.message || 'Failed to save changes' });
      } finally {
        setLoading({ ...loading, mission: false });
      }
    } else {
      // Handle other sections
      console.log(`Saving ${section} data:`, section === 'hero' ? heroData : missionData);
    }
  };

  const handleSaveCarouselOption = async () => {
    try {
      setLoading({ ...loading, carousel: true });
      setError({ ...error, carousel: null });
      setSuccess({ ...success, carousel: null });
      
      // Send the carousel option to the server
      const response = await axios.post('https://realestate.bonlineco.com/api/hero/carousel/option', {
        use_carousel: useCarouselOption
      });
      
      if (response.data.status === 'success') {
        setSuccess({ ...success, carousel: 'Carousel setting saved successfully!' });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, carousel: null }));
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to save carousel option:', error);
      setError({ ...error, carousel: error.response?.data?.message || 'Failed to save carousel setting' });
    } finally {
      setLoading({ ...loading, carousel: false });
    }
  };
  
  const handleCarouselImageChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setCarouselImageData(prev => ({
      ...prev,
      [type]: file
    }));
  };
  
  const handleCarouselImageUpload = async () => {
    // Determine which image is selected based on active tab
    const activeTabImage = carouselImageTab === 'desktop' ? carouselImageData.image : 
                          carouselImageTab === 'tablet' ? carouselImageData.tablet_image : 
                          carouselImageData.mobile_image;
    
    // Check if an image is selected for the active tab
    if (!activeTabImage) {
      setError({ ...error, carousel: `Please select an image for ${carouselImageTab} view` });
      return;
    }
    
    try {
      setLoading({ ...loading, carousel: true });
      setError({ ...error, carousel: null });
      setSuccess({ ...success, carousel: null });
      
      const formData = new FormData();
      
      // Only append the image for the active tab
      if (carouselImageTab === 'desktop') {
        formData.append('image', carouselImageData.image);
        formData.append('device_type', 'desktop');
      } else if (carouselImageTab === 'tablet') {
        formData.append('tablet_image', carouselImageData.tablet_image);
        formData.append('device_type', 'tablet');
      } else if (carouselImageTab === 'mobile') {
        formData.append('mobile_image', carouselImageData.mobile_image);
        formData.append('device_type', 'mobile');
      }
      
      const response = await axios.post('https://realestate.bonlineco.com/api/hero/carousel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.status === 'success') {
        // Add the new image to the carousel images array
        setCarouselImages([...carouselImages, response.data.data]);
        setSuccess({ ...success, carousel: 'Carousel image uploaded successfully!' });
        
        // Reset only the current tab's image
        setCarouselImageData(prev => ({
          ...prev,
          [carouselImageTab === 'desktop' ? 'image' : 
           carouselImageTab === 'tablet' ? 'tablet_image' : 'mobile_image']: null
        }));
        
        // Clear file inputs
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach(input => {
          input.value = '';
        });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, carousel: null }));
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to upload carousel image:', error);
      setError({ ...error, carousel: error.response?.data?.message || 'Failed to upload carousel image' });
    } finally {
      setLoading({ ...loading, carousel: false });
    }
  };
  const handleDeleteCarouselImage = async (id) => {
    try {
      setLoading({ ...loading, carousel: true });
      setError({ ...error, carousel: null });
      setSuccess({ ...success, carousel: null });
      
      const response = await axios.delete(`https://realestate.bonlineco.com/api/hero/carousel/${id}`);
      
      if (response.data.status === 'success') {
        // Remove the deleted image from the carousel images array
        setCarouselImages(carouselImages.filter(image => image.id !== id));
        
        setSuccess({ ...success, carousel: 'Carousel image deleted successfully!' });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, carousel: null }));
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to delete carousel image:', error);
      setError({ ...error, carousel: error.response?.data?.message || 'Failed to delete image' });
    } finally {
      setLoading({ ...loading, carousel: false });
    }
  };
  
  const handleMoveCarouselImage = async (id, direction) => {
    const currentIndex = carouselImages.findIndex(image => image.id === id);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Check if the new index is valid
    if (newIndex < 0 || newIndex >= carouselImages.length) return;
    
    // Create a copy of the carousel images array
    const updatedImages = [...carouselImages];
    
    // Swap the images
    const temp = updatedImages[currentIndex];
    updatedImages[currentIndex] = updatedImages[newIndex];
    updatedImages[newIndex] = temp;
    
    // Update the display order
    const imagesWithUpdatedOrder = updatedImages.map((image, index) => ({
      ...image,
      display_order: index + 1
    }));
    
    try {
      setLoading({ ...loading, carousel: true });
      
      const response = await axios.post('https://realestate.bonlineco.com/api/hero/carousel/order', {
        images: imagesWithUpdatedOrder.map(image => ({
          id: image.id,
          display_order: image.display_order
        }))
      });
      
      if (response.data.status === 'success') {
        // Update the carousel images array with the new order
        setCarouselImages(imagesWithUpdatedOrder);
      }
    } catch (error) {
      console.error('Failed to update carousel order:', error);
      setError({ ...error, carousel: error.response?.data?.message || 'Failed to update order' });
    } finally {
      setLoading({ ...loading, carousel: false });
    }
  };
  
  const renderHeroForm = () => (
    <Card className="content-card mt-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Card.Title>Hero Section</Card.Title>
        </div>
        
        <Tab.Container id="hero-tabs" activeKey={heroTab} onSelect={(k) => setHeroTab(k)}>
          <Nav variant="pills" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="single">Single Image</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="carousel">Carousel Images</Nav.Link>
            </Nav.Item>
          </Nav>
          
          <Tab.Content>
        <Tab.Pane eventKey="single">
          {error.hero && (
            <div className="alert alert-danger mb-4">
              {error.hero}
            </div>
          )}
          {success.hero && (
            <div className="alert alert-success mb-4">
              {success.hero}
            </div>
          )}
          <Form onSubmit={handleHeroSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-4">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={heroData.title}
                  onChange={(e) => setHeroData({ ...heroData, title: e.target.value })}
                  disabled={loading.hero}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Subtitle</Form.Label>
                <Form.Control
                  type="text"
                  value={heroData.subtitle}
                  onChange={(e) => setHeroData({ ...heroData, subtitle: e.target.value })}
                  disabled={loading.hero}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Background Images</Form.Label>
                <Nav variant="tabs" className="mb-3">
                  <Nav.Item>
                    <Nav.Link 
                      active={activeImageTab === 'desktop'}
                      onClick={() => setActiveImageTab('desktop')}
                    >
                      Desktop
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeImageTab === 'tablet'}
                      onClick={() => setActiveImageTab('tablet')}
                    >
                      Tablet
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeImageTab === 'mobile'}
                      onClick={() => setActiveImageTab('mobile')}
                    >
                      Mobile
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                
                <div className="tab-content">
                  {/* Desktop Image */}
                  <div className={`tab-pane ${activeImageTab === 'desktop' ? 'active' : ''}`}>
                    <Form.Label>Desktop Image</Form.Label>
                    <Form.Control
                      type="file"
                      onChange={(e) => setHeroData({ ...heroData, image: e.target.files[0] })}
                      disabled={loading.hero}
                      accept="image/*"
                    />
                    <small className="text-muted">Recommended size: 1920x1080px</small>
                  </div>
                  
                  {/* Tablet Image */}
                  <div className={`tab-pane ${activeImageTab === 'tablet' ? 'active' : ''}`}>
                    <Form.Label>Tablet Image</Form.Label>
                    <Form.Control
                      type="file"
                      onChange={(e) => setHeroData({ ...heroData, tablet_image: e.target.files[0] })}
                      disabled={loading.hero}
                      accept="image/*"
                    />
                    <small className="text-muted">Recommended size: 1024x768px</small>
                  </div>
                  
                  {/* Mobile Image */}
                  <div className={`tab-pane ${activeImageTab === 'mobile' ? 'active' : ''}`}>
                    <Form.Label>Mobile Image</Form.Label>
                    <Form.Control
                      type="file"
                      onChange={(e) => setHeroData({ ...heroData, mobile_image: e.target.files[0] })}
                      disabled={loading.hero}
                      accept="image/*"
                    />
                    <small className="text-muted">Recommended size: 640x960px</small>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Button type="submit" disabled={loading.hero}>
            {loading.hero ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Form>
        </Tab.Pane>
        
        <Tab.Pane eventKey="carousel">
          {error.carousel && (
            <div className="alert alert-danger mb-4">
              {error.carousel}
            </div>
          )}
          {success.carousel && (
            <div className="alert alert-success mb-4">
              {success.carousel}
            </div>
          )}
          
          <Form>
            <div className="mb-4">
              <Form.Check 
                type="switch"
                id="carousel-toggle"
                label="Use carousel instead of single image"
                checked={useCarouselOption}
                onChange={(e) => setUseCarouselOption(e.target.checked)}
                className="mb-3"
              />
              <div className="text-muted mb-3">
                <small>When enabled, the carousel will be shown instead of the single hero image.</small>
              </div>
              
              <Button 
                variant="primary" 
                onClick={handleSaveCarouselOption}
                disabled={loading.carousel}
                className="mb-4"
              >
                {loading.carousel ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>Save Carousel Setting</>
                )}
              </Button>
            </div>
            
            <div className="mb-4">
              <h5>Upload New Carousel Image</h5>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link 
                    active={carouselImageTab === 'desktop'}
                    onClick={() => setCarouselImageTab('desktop')}
                  >
                    Desktop
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    active={carouselImageTab === 'tablet'}
                    onClick={() => setCarouselImageTab('tablet')}
                  >
                    Tablet
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    active={carouselImageTab === 'mobile'}
                    onClick={() => setCarouselImageTab('mobile')}
                  >
                    Mobile
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              
              <div className="tab-content mb-3">
                {/* Desktop Image */}
                <div className={`tab-pane ${carouselImageTab === 'desktop' ? 'active' : ''}`}>
                  <Form.Label>Desktop Image</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => handleCarouselImageChange(e, 'image')}
                    disabled={loading.carousel}
                    accept="image/*"
                  />
                  <small className="text-muted">Recommended size: 1920x1080px</small>
                </div>
                
                {/* Tablet Image */}
                <div className={`tab-pane ${carouselImageTab === 'tablet' ? 'active' : ''}`}>
                  <Form.Label>Tablet Image</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => handleCarouselImageChange(e, 'tablet_image')}
                    disabled={loading.carousel}
                    accept="image/*"
                  />
                  <small className="text-muted">Recommended size: 1024x768px</small>
                </div>
                
                {/* Mobile Image */}
                <div className={`tab-pane ${carouselImageTab === 'mobile' ? 'active' : ''}`}>
                  <Form.Label>Mobile Image</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => handleCarouselImageChange(e, 'mobile_image')}
                    disabled={loading.carousel}
                    accept="image/*"
                  />
                  <small className="text-muted">Recommended size: 640x960px</small>
                </div>
              </div>
              
              {/* Device type is now determined by which tab is active */}
              
              <Button
                variant="primary"
                onClick={handleCarouselImageUpload}
                disabled={loading.carousel}
                className="mt-2"
              >
                {loading.carousel ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaUpload className="me-2" /> Upload Carousel Image
                  </>
                )}
              </Button>
              <small className="d-block text-muted mt-2">You can upload up to 3 images for the carousel</small>
            </div>
          </Form>
          
          <h5>Current Carousel Images</h5>
          {carouselImages.length === 0 ? (
            <p>No carousel images uploaded yet.</p>
          ) : (
            <div className="carousel-images-container">
              {carouselImages.map((image, index) => (
                <div key={image.id} className="carousel-image-item mb-3 p-3 border rounded">
                  <div className="d-flex align-items-center">
                    <div className="carousel-image-preview me-3">
                      <img 
                        src={`https://realestate.bonlineco.com/api/image/${image.image_path.replace(/\\/g, '/')}`} 
                        alt={`Carousel ${index + 1}`} 
                        style={{ width: '100px', height: '60px', objectFit: 'cover' }} 
                      />
                    </div>
                    <div className="flex-grow-1">
                      <p className="mb-1">Image {index + 1}</p>
                      <div className="d-flex">
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          className="me-2"
                          onClick={() => handleDeleteCarouselImage(image.id)}
                          disabled={loading.carousel}
                        >
                          <FaTrash /> Delete
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          className="me-2"
                          onClick={() => handleMoveCarouselImage(image.id, 'up')}
                          disabled={index === 0 || loading.carousel}
                        >
                          <FaArrowUp /> Move Up
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => handleMoveCarouselImage(image.id, 'down')}
                          disabled={index === carouselImages.length - 1 || loading.carousel}
                        >
                          <FaArrowDown /> Move Down
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Card.Body>
    </Card>
  );

  const renderForm = (section, data, setData) => (
    <Card className="content-card mt-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Card.Title>{section.charAt(0).toUpperCase() + section.slice(1)} Section</Card.Title>
          <button 
            className="btn btn-primary" 
            onClick={() => handleSave(section)}
            disabled={loading[section]}
          >
            {loading[section] ? 'Saving...' : (
              <>
                <FaSave className="me-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
        {error[section] && (
          <div className="alert alert-danger mb-4">
            {error[section]}
          </div>
        )}
        {success[section] && (
          <div className="alert alert-success mb-4">
            {success[section]}
          </div>
        )}
        <Form>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-4">
                <Form.Label>Header</Form.Label>
                {section === 'hero' ? (
                  <Form.Control
                    type="text"
                    value={data.header}
                    onChange={(e) => setData({ ...data, header: e.target.value })}
                    placeholder="Enter header text"
                  />
                ) : (
                  <Form.Control
                    type="text"
                    value={data.header}
                    readOnly
                    disabled
                    className="bg-light"
                  />
                )}
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Paragraph</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={data.paragraph}
                  onChange={(e) => setData({ ...data, paragraph: e.target.value })}
                  placeholder="Enter paragraph text"
                  disabled={loading[section]}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-4">
                <Form.Label>Image</Form.Label>
                <div className="image-upload-container">
                  <div className="current-image">
                    <img src={data.img} alt={`${section} preview`} className="img-preview" />
                  </div>
                  <div className="upload-button-container">
                    <label className={`btn btn-outline-primary w-100 ${loading[section] ? 'disabled' : ''}`}>
                      <FaUpload className="me-2" />
                      {loading[section] ? 'Uploading...' : 'Upload New Image'}
                      <Form.Control
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) => handleImageUpload(section, e)}
                        disabled={loading[section]}
                      />
                    </label>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );

  return (
    <div className="home-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title">Home Page Management</h2>
      </div>

      <Nav variant="tabs" activeKey={key} onSelect={(k) => setKey(k)}>
        <Nav.Item>
          <Nav.Link eventKey="hero">Hero Section</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="vision">Vision Section</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="mission">Mission Section</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="services">Services Section</Nav.Link>
        </Nav.Item>
      </Nav>

      {key === 'hero' && renderHeroForm()}
      {key === 'vision' && renderForm('vision', visionData, setVisionData)}
      {key === 'mission' && renderForm('mission', missionData, setMissionData)}
      {key === 'services' && <ServicesManagement />}
    </div>
  );
};

export default HomeManagement;
